import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Settings, LogOut } from 'react-feather';
import {
  UserCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import {
  Menu,
  MenuVariant,
  MenuSize,
  type MenuItemProps,
} from '../../components/Menu';
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogoutDialog } from '../../components/LogoutDialog';
import SupportModal from './SupportModal';
import { Loader } from '../../components/Loader';
import { apiClient } from '../../services/api';
import { fetchProfile } from '@/epics/userProfile/api/viewProfileApiService';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';

interface UserProfileDropdownProps {
  /**
   * User's full name
   */
  userName?: string;
  /**
   * User's role/title
   */
  userRole?: string;
  /**
   * User's initials for avatar (if not provided, will be generated from userName)
   */
  userInitials?: string;
  /**
   * Custom menu items (optional)
   */
  menuItems?: MenuItemProps[];
  /**
   * Callback when logout is clicked
   */
  onLogout?: () => void;
}

/**
 * UserProfileDropdown Component
 * Displays user profile information with a dropdown menu
 */
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Administrator',
  ADMIN: 'Administrator',
  CLIENT: 'Klient',
  TECHNICIAN: 'Technik',
};

const getRoleLabel = (role: string | null | undefined): string =>
  (role && ROLE_LABELS[role]) || '';

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  userName: userNameProp,
  userRole: userRoleProp,
  userInitials,
  menuItems,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [profileName, setProfileName] = useState(userNameProp ?? '');
  const [profileRole, setProfileRole] = useState(userRoleProp ?? '');
  const [profileRoleRaw, setProfileRoleRaw] = useState<string | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string>();
  const [imgError, setImgError] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isOnProfilePage = location.pathname === '/Profil';

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  // Fetch profile data on mount
  useEffect(() => {
    let cancelled = false;
    fetchProfile().then((result) => {
      if (cancelled) {
        return;
      }
      if (result.success && result.data) {
        setProfileName(result.data.name ?? '');
        setProfileRole(getRoleLabel(result.data.role));
        setProfileRoleRaw(result.data.role);
        const url = result.data.profilePicture?.url?.trim();
        setProfileImageUrl(url || undefined);
        setImgError(false);
      }
      setIsProfileLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const isClient = profileRoleRaw === 'CLIENT';
  const contactRequestDisabled =
    isProfileLoading ||
    privilegesLoading ||
    !privileges.canCreateContactRequest;

  const userName = profileName || userNameProp || '';
  const userRole = profileRole || userRoleProp || '';

  // Generate initials from userName if not provided
  const getInitials = () => {
    if (userInitials) {
      return userInitials;
    }

    const name = userName;
    if (!name) {
      return '';
    }

    const names = name.trim().split(' ').filter(Boolean);

    if (names.length >= 2) {
      const first = names[0];
      const last = names[names.length - 1];
      if (first && last && first[0] && last[0]) {
        return `${first[0]}${last[0]}`.toUpperCase();
      }
    }

    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials();

  // Default menu items
  const contactRequestItem: MenuItemProps = {
    id: 'contactRequest',
    label: 'Utwórz zgłoszenie kontaktowe',
    fontWeight: 500,
    toolTip: false,
    leadingIcon: <ChatBubbleLeftEllipsisIcon width={18} height={18} />,
    textColor: contactRequestDisabled ? '#878B8E' : '#181A1B',
    iconColor: contactRequestDisabled ? '#878B8E' : '#181A1B',
    disabled: contactRequestDisabled,
    disabledTooltip: 'Nie masz uprawnień do wykonania tej czynności',
    disabledTooltipSide: 'right',
    onClick: (_id: string) => {
      setIsOpen(false);
      navigate('/Zgłoszenia-kontaktowe/Utwórz-zgłoszenie-kontaktowe');
    },
  };

  const defaultMenuItems: MenuItemProps[] = [
    {
      id: 'profile',
      label: 'Profil',
      fontWeight: 500,
      textColor: '#181A1B',
      iconColor: '#181A1B',
      selected: isOnProfilePage,
      toolTip: false,
      leadingIcon: (
        <UserCircleIcon
          width={18}
          height={18}
          style={{
            width: '18px',
            height: '18px',
            transformOrigin: 'center center',
            display: 'block',
            margin: '2px',
          }}
        />
      ),
      onClick: (_id: string) => {
        setIsOpen(false);
        navigate('/Profil');
      },
    },
    {
      id: 'Support',
      label: 'Kontakt z pomocą techniczną',
      fontWeight: 500,
      toolTip: false,
      leadingIcon: (
        <EnvelopeIcon
          width={18}
          height={18}
          style={{
            width: '18px',
            height: '18px',
            transformOrigin: 'center center',
            display: 'block',
            marginLeft: '2px',
          }}
        />
      ),
      textColor: '#181A1B',
      iconColor: '#181A1B',
      onClick: (_id: string) => {
        setIsOpen(false);
        setIsSupportModalOpen(true);
      },
    },
    ...(isClient ? [contactRequestItem] : []),
    {
      id: 'settings',
      label: 'Settings',
      fontWeight: 500,
      toolTip: false,
      leadingIcon: <Settings size={18} />,
      textColor: '#181A1B',
      iconColor: '#181A1B',
      divider: true,
      onClick: (_id: string) => {
        setIsOpen(false);
        // Handle settings navigation
      },
    },
    {
      id: 'Wyloguj',
      label: 'Wyloguj',
      fontWeight: 500,
      toolTip: false,
      leadingIcon: <LogOut size={18} />,
      textColor: '#E11B1B',
      iconColor: '#E11B1B',
      className: 'mt-1',
      onClick: (_id: string) => {
        setIsOpen(false);
        setIsLogoutDialogOpen(true);
      },
    },
  ];

  const items = menuItems || defaultMenuItems;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle Escape key to close dropdown
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleMenuClick = (id: string) => {
    const item = items.find((item) => item.id === id);
    if (item?.onClick) {
      item.onClick(id);
    }
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    setIsLogoutDialogOpen(false);
    setShowLoader(true);

    // For test accounts (pozytron.test+), skip the backend globalSignOut call
    // so Cognito refresh tokens for parallel E2E scenarios are not revoked
    let isTestAccount = false;
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      const email = session.tokens?.idToken?.payload?.['email'];
      isTestAccount =
        typeof email === 'string' && email.startsWith('pozytron.test');
    } catch {
      // Cannot determine email — proceed with normal logout
    }

    if (!isTestAccount) {
      try {
        await apiClient.post('/loopay-user-management/auth/logout');
      } catch (error) {
        console.error('Backend logout failed:', error);
      }
    }

    try {
      await signOut();
    } catch (error) {
      console.warn('Cognito sign out failed:', error);
    }

    try {
      localStorage.clear();
      sessionStorage.clear();

      if (window.indexedDB) {
        const databases = await window.indexedDB.databases();
        databases.forEach((db) => {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        });
      }
    } catch (error) {
      console.warn('Storage cleanup failed:', error);
    }

    if (!isTestAccount) {
      try {
        localStorage.setItem('logout-event', Date.now().toString());
      } catch (error) {
        console.warn('Failed to set logout sync:', error);
      }
    }

    setShowLoader(false);
    setIsLoggingOut(false);
    navigate('/login?status=logged-out', { replace: true });
  };

  const handleLogoutDialogClose = () => {
    if (!isLoggingOut) {
      setIsLogoutDialogOpen(false);
    }
  };

  const handleSupportModalClose = () => {
    setIsSupportModalOpen(false);
  };

  const handleSupportModalConfirm = () => {
    // Add logic here if confirmation requires action
    setIsSupportModalOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Profile Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] focus-visible:ring-offset-2"
        data-testid="user-profile-dropdown-button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Profil użytkownika, ${userName}`}
        style={{ height: '56px' }}
      >
        {/* Avatar */}
        <div className="shrink-0">
          {isProfileLoading ? (
            <div
              className="rounded-full bg-gray-300 animate-pulse"
              style={{ width: '40px', height: '40px' }}
            />
          ) : (
            <div
              className="flex items-center justify-center shrink-0 rounded-full bg-[#E8EEF0] text-[#223746] text-[14px] overflow-hidden"
              style={{
                width: '40px',
                height: '40px',
                lineHeight: '20px',
              }}
            >
              {profileImageUrl && !imgError ? (
                <img
                  src={profileImageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                initials
              )}
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="flex flex-col items-start min-w-0 max-w-[140px] text-neutral-10">
          {isProfileLoading ? (
            <>
              <div
                className="bg-gray-300 rounded animate-pulse"
                style={{ width: '100px', height: '14px', marginBottom: '4px' }}
              />
              <div
                className="bg-gray-300 rounded animate-pulse"
                style={{ width: '70px', height: '11px' }}
              />
            </>
          ) : (
            <>
              <span
                className="text-left truncate w-full"
                style={{
                  fontSize: '14px',
                  lineHeight: '24px',
                  fontWeight: 500,
                }}
              >
                {userName}
              </span>
              <span
                className="text-left truncate w-full"
                style={{
                  fontSize: '11px',
                  lineHeight: '20px',
                  fontWeight: 500,
                  color: '#878B8E',
                }}
              >
                {userRole}
              </span>
            </>
          )}
        </div>

        {/* Chevron Icon */}
        <ChevronDown
          data-testid="user-profile-dropdown-icon"
          size={20}
          className={`shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          style={{ color: '#1a2b37' }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 mt-2"
          style={{
            right: 0,
            top: '100%',
          }}
        >
          <Menu
            items={items}
            variant={MenuVariant.Light}
            size={MenuSize.Medium}
            showLeadingIcons={true}
            showTrailingArrows={false}
            onItemClick={handleMenuClick}
            width="max-content"
          />
        </div>
      )}

      {/* Logout Dialog */}
      <LogoutDialog
        open={isLogoutDialogOpen}
        onClose={handleLogoutDialogClose}
        onConfirm={handleLogoutConfirm}
        isLoggingOut={isLoggingOut}
      />

      {/* Support Dialog */}
      <SupportModal
        open={isSupportModalOpen}
        onClose={handleSupportModalClose}
        onConfirmCancel={handleSupportModalConfirm}
      />

      {/* Full Screen Loader - Rendered via portal to cover entire app */}
      {showLoader &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white">
            <Loader text="Wylogowywanie..." />
          </div>,
          document.body
        )}
    </div>
  );
};

export default UserProfileDropdown;
